<script setup lang="ts">
import { computed } from 'vue';

export interface Props {
  message?: string;
  icon?: string;
  type?: 'warning' | 'info';
  clickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
  icon: 'mdi-information',
  type: 'warning',
  clickable: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const rootTag = computed(() => (props.clickable ? 'button' : 'div'));
</script>

<template>
  <component
    :is="rootTag"
    :type="props.clickable ? 'button' : undefined"
    :class="[
      $style.jobBookingWarningBanner,
      $style[`banner-${props.type}`],
      props.clickable && $style.clickable,
    ]"
    data-testid="jobBookingWarningBanner"
    @click="props.clickable && emit('click', $event)"
  >
    <slot>
      <v-icon :icon="props.icon" size="16" :class="$style.icon" />
      <span>{{ props.message }}</span>
    </slot>
    <v-icon
      v-if="props.clickable"
      icon="mdi-chevron-right"
      size="18"
      :class="$style.chevron"
    />
  </component>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.jobBookingWarningBanner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.banner-warning {
  background: colors.$color-orange-50;
  color: colors.$color-text-primary;

  &.clickable:hover {
    border-color: colors.$color-orange-300;
  }
}

.banner-info {
  background: colors.$color-gray-50;
  color: colors.$color-text-secondary;

  &.clickable:hover {
    background-color: colors.$color-brand-50;
    border-color: colors.$color-brand-400;
  }
}

.icon {
  flex-shrink: 0;
}
  .banner-warning .icon {
    color: colors.$color-yellow-500;
  }
  .banner-info .icon {
    color: colors.$color-brand-500;
  }

.clickable {
  appearance: none;
  font-family: inherit;
  line-height: inherit;
  text-align: left;
  cursor: pointer;
}

.chevron {
  margin-left: auto;
  flex-shrink: 0;
  color: colors.$color-text-tertiary;
}
</style>
